import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Text } from "@performyard/py-components";
import {
	MeetingWorkspaceInsightResponseStreamJson,
	MetaDataMeetingWorkspaceInsightResponseStreamJson,
	MetaDataTeamMeetingWorkspaceInsightResponseStreamJson,
	TeamMeetingWorkspaceInsightResponseStreamJson,
	UserErrorResponseJson,
} from "@performyard/service-contracts";
import { formatDateAsUTCString } from "@performyard/utils-isomorphic";

import { useAiQuery } from "../../../api/hooks/defs/ai";

import { InsightsGeneratedAt } from "../InsightsGeneratedAt";
import { AISidePanel } from "../SidePanel";

export type MeetingWorkspaceInsightModuleProps = {
	workspaceId: string;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
	filters: Record<string, unknown>;
	isAdmin: boolean;
	allMeetingsCancelled?: boolean;
	isTeamWorkspace?: boolean;
};

// Combined map covers both 1:1 and team response shapes. Promoted workspaces with 2 current
// participants still receive a team-shaped response from the server, so the renderer must
// resolve titles for both key sets regardless of which type the client sent.
const SECTION_TITLES: Record<string, string> = {
	executiveSummary: "Executive Summary",
	// 1:1 keys
	progressAndGrowth: "Progress & Growth",
	developmentAreasAndGrowthOpportunities: "Development Areas & Growth Opportunities",
	// team keys
	recurringThemesAndTopics: "Recurring Themes & Topics",
	recurringChallengesAndBlockers: "Recurring Challenges & Blockers",
	// shared keys
	keyAccomplishmentsAndWins: "Key Accomplishments & Wins",
	notableDiscussionsAndDecisions: "Notable Discussions & Decisions",
	actionItemAnalysis: "Action Item Analysis",
};

type InsightResponseJson =
	| MetaDataMeetingWorkspaceInsightResponseStreamJson
	| MetaDataTeamMeetingWorkspaceInsightResponseStreamJson;

type InsightDataJson = MeetingWorkspaceInsightResponseStreamJson | TeamMeetingWorkspaceInsightResponseStreamJson;

const isInsightResponseError = (
	response: UserErrorResponseJson | InsightResponseJson | undefined,
	inProgress: boolean,
	allMeetingsCancelled: boolean
): response is UserErrorResponseJson => {
	if (!response || inProgress) return false;
	if (allMeetingsCancelled) return true;
	if (typeof response === "object" && "status" in response) return false;
	if (
		typeof response === "object" &&
		"data" in response &&
		typeof (response as { data?: unknown }).data === "object" &&
		response.data !== null &&
		"message" in (response.data as Record<string, unknown>)
	) {
		return true;
	}
	return false;
};

export const MeetingWorkspaceInsightModule = ({
	workspaceId,
	isOpen,
	onOpenChange,
	filters,
	isAdmin,
	allMeetingsCancelled = false,
	isTeamWorkspace = false,
}: MeetingWorkspaceInsightModuleProps) => {
	const [recreateCount, setRecreateCount] = useState(0);

	const queryType = isTeamWorkspace ? "team-meeting-workspace-insight" : "meeting-workspace-insight";
	const queryFilters = useMemo(() => {
		if (!isTeamWorkspace) return filters;
		const { privateNotes: _privateNotes, ...teamFilters } = filters;
		return teamFilters;
	}, [filters, isTeamWorkspace]);

	const { data, isLoading, isFetching, isRefetching } = useAiQuery<InsightResponseJson | UserErrorResponseJson>({
		type: queryType,
		workspaceId,
		filters: queryFilters,
		findAdditionalContext: false,
		retry: recreateCount > 0 ? "true" : "false",
		responseStyle: "structured",
		recreateCount,
		enabled: isOpen,
	});

	const inProgress = useMemo(() => {
		if (isLoading || isRefetching) return true;
		if (data && typeof data === "object" && "status" in data) {
			return data.status === "in_progress";
		}
		return false;
	}, [data, isLoading, isRefetching]);

	const cancelledMeetingsError = useMemo<UserErrorResponseJson | null>(
		() =>
			allMeetingsCancelled
				? {
						message:
							"Insights are unavailable for this meeting workspace. Please ensure that at least one meeting does not have a status of canceled.",
					}
				: null,
		[allMeetingsCancelled]
	);

	const genericErrorMessage = "The generation of Meeting Workspace Insights was unsuccessful. Please try again.";
	const resolvedError = useMemo<UserErrorResponseJson | null>(() => {
		if (cancelledMeetingsError) return cancelledMeetingsError;
		if (data && isInsightResponseError(data, inProgress, allMeetingsCancelled)) {
			// Surface a friendlier message without mutating the underlying response object.
			return { ...data, message: genericErrorMessage };
		}
		return null;
	}, [data, cancelledMeetingsError, allMeetingsCancelled, inProgress]);

	const handleRetry = useCallback(() => {
		setRecreateCount((prev) => prev + 1);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			setRecreateCount(0);
		}
	}, [isOpen]);

	const renderHeader = (data: InsightDataJson) => {
		return (
			<div className="border-radius-1 flex w-full flex-col items-center justify-center gap-4 self-stretch border-b border-solid border-neutral-200 pb-4">
				<div className="flex items-start gap-5 self-stretch rounded bg-neutral-100 p-2">
					<div className="flex flex-1 shrink-0 basis-0 flex-col items-start gap-2">
						<Text size="paragraph-2" weight="semi-bold">
							{data.workspaceName}
						</Text>
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
							Meeting Dates:{" "}
							<Text size="paragraph-3" weight="regular" color="neutral-9" overrideElement="span">
								{formatDateAsUTCString(new Date(data.startDate))}-
								{formatDateAsUTCString(new Date(data.endDate))}
							</Text>
						</Text>
					</div>
				</div>
			</div>
		);
	};

	const renderInfoSection = (): React.ReactNode => {
		if (!data || isInsightResponseError(data, inProgress, allMeetingsCancelled)) return null;

		const generatedBy = data.createdBy || data.data?.createdBy || null;

		// Per AC: for team meeting workspaces every viewer (participant or admin) sees who
		// generated the insight. For 1:1 workspaces only admins see the generator's name
		// because participants generate their own personalized insights.
		const showGeneratedBy = (isAdmin || isTeamWorkspace) && generatedBy;

		return (
			<InsightsGeneratedAt
				label="Insights Populated"
				date={data.createdAt}
				generatedBy={showGeneratedBy ? generatedBy : null}
			/>
		);
	};

	return (
		<AISidePanel<InsightResponseJson>
			type="meeting"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			data={data && !isInsightResponseError(data, inProgress, allMeetingsCancelled) ? data : null}
			isLoading={isLoading || isFetching}
			onRetry={handleRetry}
			renderHeader={renderHeader}
			renderInfoSection={renderInfoSection}
			error={resolvedError}
			logoLoaderProps={{
				textArray: [
					"Compiling Meetings…",
					"Organizing Meetings Content…",
					"Processing Agendas…",
					"Processing Action Items…",
					"Processing Notes…",
					"Generating Insights…",
					"Structuring Insights Format…",
					"Finalizing Results…",
					"Finishing Up…",
				],
				mainText: "Summarizing Meetings",
			}}
			sectionTitleMap={SECTION_TITLES}
		/>
	);
};
